// The contact pages' hardcoded <aside> info column (Sleep Expert / Customer
// Support cards): a faithful port of the identical aside markup in the legacy
// cms/contact.html + cms/contact_landing.html. All copy arrives pre-localized
// as string props (server-side gettext; fr_CA is live and React `t` has no
// runtime locale). The badge icons were `{% static_image %}` (lazysizes) --
// rendered as eager <img> per the platform-wide accepted divergence. The hours
// html is the legacy `<div>{{ ... |richtext }}</div>` pair: the outer literal
// div here, the inner `.rich-text` div inside the server-rendered string.
import { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { templates } from "@reactivated";

type IAsideData = templates.ContactFormPageTemplate["aside"];

type IProps = Immutable<{
    aside: IAsideData;
    // cms/contact.html rendered the phone display inside an <h3> (piped
    // through |phone_num_with_hyphens server-side); cms/contact_landing.html
    // inside a <p class="type_x0point875"> (raw as_national). A real legacy
    // divergence between the two pages, preserved.
    phoneVariant: "heading" | "small";
}>;

const PhoneDisplay = (
    props: Immutable<{ variant: IProps["phoneVariant"]; display: string }>,
): React.JSX.Element =>
    props.variant === "heading" ? (
        <h3>{props.display}</h3>
    ) : (
        <p className="type_x0point875">{props.display}</p>
    );

export const ContactUsAside = (props: IProps): React.JSX.Element => {
    const { aside, phoneVariant } = props;
    const staticURL = useStaticURL();
    return (
        <>
            <div>
                <img
                    src={`${staticURL}img/icons/badge-sleep-expert-white.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>
                    <strong>{aside.sleep_expert_heading}</strong>
                </h3>
                <p>{aside.sleep_expert_copy}</p>
                <a href={aside.pre_order_phone_href}>
                    <PhoneDisplay
                        variant={phoneVariant}
                        display={aside.pre_order_phone_display}
                    />
                </a>
                <div
                    dangerouslySetInnerHTML={{ __html: aside.hours_top_html }}
                />
            </div>

            <div>
                <img
                    src={`${staticURL}img/icons/badge-headset-white.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>
                    <strong>{aside.customer_support_heading}</strong>
                </h3>
                <p>{aside.customer_support_copy}</p>
                <a href={aside.customer_service_phone_href}>
                    <PhoneDisplay
                        variant={phoneVariant}
                        display={aside.customer_service_phone_display}
                    />
                </a>
                <div
                    dangerouslySetInnerHTML={{
                        __html: aside.hours_bottom_html,
                    }}
                />
            </div>
        </>
    );
};
